import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { providersAPI, adminAPI } from '../services/api'
import { toast } from 'react-hot-toast'

export const useProviderActions = () => {
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const toggleProvider = async (providerId) => {
    setIsLoading(true)
    try {
      const response = await providersAPI.toggleProvider(providerId)

      // Invalidate and refetch providers data
      queryClient.invalidateQueries(['admin', 'providers'])
      
      const message = response.data.message || 'Provider durumu değiştirildi'
      toast.success(message)
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Provider durumu değiştirilemedi'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const syncPrices = async (providerId = null) => {
    setIsLoading(true)
    try {
      let response
      
      if (providerId) {
        // Sync specific provider
        response = await adminAPI.post(`/providers/${providerId}/sync-prices`)
      } else {
        // Sync all providers
        response = await providersAPI.syncPrices()
      }

      // Invalidate and refetch providers data
      queryClient.invalidateQueries(['admin', 'providers'])
      queryClient.invalidateQueries(['admin', 'provider-prices'])
      
      const message = response.data.message || 'Fiyat senkronizasyonu başlatıldı'
      toast.success(message)
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Fiyat senkronizasyonu başlatılamadı'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const updateProviderConfig = async (providerId, config) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.put(`/providers/${providerId}/config`, config)

      // Invalidate and refetch provider data
      queryClient.invalidateQueries(['admin', 'providers'])
      queryClient.invalidateQueries(['admin', 'provider-details', providerId])
      
      toast.success('Provider yapılandırması güncellendi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Provider yapılandırması güncellenemedi'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const testProviderConnection = async (providerId) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.post(`/providers/${providerId}/test-connection`)
      
      toast.success('Provider bağlantısı başarıyla test edildi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Provider bağlantı testi başarısız'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const addProvider = async (providerData) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.post('/providers', providerData)

      // Invalidate and refetch providers data
      queryClient.invalidateQueries(['admin', 'providers'])
      
      toast.success('Yeni provider başarıyla eklendi')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Provider eklenemedi'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const removeProvider = async (providerId) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.delete(`/providers/${providerId}`)

      // Invalidate and refetch providers data
      queryClient.invalidateQueries(['admin', 'providers'])
      
      toast.success('Provider başarıyla kaldırıldı')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Provider kaldırılamadı'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const resetProviderPrices = async (providerId) => {
    setIsLoading(true)
    try {
      const response = await adminAPI.delete(`/providers/${providerId}/prices`)

      // Invalidate and refetch provider data
      queryClient.invalidateQueries(['admin', 'providers'])
      queryClient.invalidateQueries(['admin', 'provider-prices', providerId])
      
      toast.success('Provider fiyatları sıfırlandı')
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Provider fiyatları sıfırlanamadı'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const getProviderLogs = async (providerId, filters = {}) => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams()
      
      if (filters.dateFrom) {
        params.append('dateFrom', filters.dateFrom.toISOString().split('T')[0])
      }
      if (filters.dateTo) {
        params.append('dateTo', filters.dateTo.toISOString().split('T')[0])
      }
      if (filters.level) {
        params.append('level', filters.level)
      }
      
      const response = await adminAPI.get(`/providers/${providerId}/logs?${params.toString()}`)
      return response.data.data
    } catch (error) {
      const message = error.response?.data?.message || 'Provider logları alınamadı'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  return {
    toggleProvider,
    syncPrices,
    updateProviderConfig,
    testProviderConnection,
    addProvider,
    removeProvider,
    resetProviderPrices,
    getProviderLogs,
    isLoading,
  }
}